import React, { useState, useRef, useEffect } from "react";
import { getSuggestions } from "./jobTitles";

const styles = `
  .as-wrap {
    position: relative;
    width: 100%;
  }

  .as-dropdown {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    background: #fff;
    border: 1.5px solid #0d7377;
    border-radius: 12px;
    box-shadow: 0 8px 28px rgba(13,115,119,0.18);
    z-index: 999;
    overflow: hidden;
    max-height: 260px;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: #b0d8dc #f5fcfd;
  }

  .as-dropdown::-webkit-scrollbar { width: 5px; }
  .as-dropdown::-webkit-scrollbar-track { background: #f5fcfd; }
  .as-dropdown::-webkit-scrollbar-thumb { background: #b0d8dc; border-radius: 10px; }

  .as-item {
    padding: 10px 16px;
    font-family: 'Nunito', sans-serif;
    font-size: 0.9rem;
    font-weight: 600;
    color: #1a3a4a;
    cursor: pointer;
    transition: background 0.15s;
    border-bottom: 1px solid #f0f8f9;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .as-item:last-child { border-bottom: none; }
  .as-item:hover, .as-item.active {
    background: linear-gradient(90deg, #e0f7f3, #f0fcfa);
    color: #0d7377;
  }
  .as-item-icon { font-size: 1rem; flex-shrink: 0; }

  .as-highlight { color: #0d7377; font-weight: 800; }

  .as-no-result {
    padding: 12px 16px;
    font-size: 0.85rem;
    color: #8aacb2;
    font-family: 'Nunito', sans-serif;
    font-style: italic;
  }
`;

/**
 * AutosuggestInput
 *
 * Props:
 *   value        {string}   – controlled value
 *   onChange     {fn}       – called with the new string value
 *   placeholder  {string}
 *   className    {string}   – extra CSS class on the <input>
 *   inputStyle   {object}   – inline styles on the <input>
 *   limit        {number}   – max suggestions (default 8)
 *   name         {string}   – input name attribute
 *   required     {bool}
 */
const AutosuggestInput = ({
  value = "",
  onChange,
  placeholder = "Search…",
  className = "",
  inputStyle = {},
  limit = 8,
  name,
  required,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [activeIdx, setActiveIdx]     = useState(-1);
  const [open, setOpen]               = useState(false);
  const wrapRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    onChange(val);
    const s = getSuggestions(val, limit);
    setSuggestions(s);
    setOpen(s.length > 0);
    setActiveIdx(-1);
  };

  const handleSelect = (title) => {
    onChange(title);
    setSuggestions([]);
    setOpen(false);
    setActiveIdx(-1);
  };

  const handleKeyDown = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, -1));
    } else if (e.key === "Enter" && activeIdx >= 0) {
      e.preventDefault();
      handleSelect(suggestions[activeIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  // Bold-highlight using innerHTML to avoid JSX whitespace gaps between text nodes
  const highlight = (text, query) => {
    if (!query) return { __html: text };
    const idx = text.toLowerCase().indexOf(query.toLowerCase());
    if (idx === -1) return { __html: text };
    const before  = text.slice(0, idx);
    const matched = text.slice(idx, idx + query.length);
    const after   = text.slice(idx + query.length);
    return {
      __html: `${before}<span class="as-highlight">${matched}</span>${after}`,
    };
  };

  const iconForTitle = (title) => {
    const t = title.toLowerCase();
    if (t.includes("nurse") || t.includes("midwife"))     return "👩‍⚕️";
    if (t.includes("dent") || t.includes("oral"))         return "🦷";
    if (t.includes("physio") || t.includes("therapy"))    return "🏃";
    if (t.includes("paramedic") || t.includes("emt") || t.includes("ambulance")) return "🚑";
    if (t.includes("ayur"))                                return "🌿";
    if (t.includes("lab") || t.includes("technician") || t.includes("technologist")) return "🔬";
    if (t.includes("pharmac"))                             return "💊";
    return "👨‍⚕️";
  };

  return (
    <>
      <style>{styles}</style>
      <div className="as-wrap" ref={wrapRef}>
        <input
          name={name}
          required={required}
          className={className}
          style={inputStyle}
          type="text"
          value={value}
          placeholder={placeholder}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            const s = getSuggestions(value, limit);
            if (s.length) { setSuggestions(s); setOpen(true); }
          }}
          autoComplete="off"
        />

        {open && (
          <div className="as-dropdown">
            {suggestions.length === 0 ? (
              <div className="as-no-result">No matching titles found</div>
            ) : (
              suggestions.map((title, i) => (
                <div
                  key={title}
                  className={`as-item ${i === activeIdx ? "active" : ""}`}
                  onMouseDown={() => handleSelect(title)}
                >
                  <span className="as-item-icon">{iconForTitle(title)}</span>
                  <span dangerouslySetInnerHTML={highlight(title, value)} />
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default AutosuggestInput;